import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Tag, Plus, Trash2, Copy, Percent, PoundSterling, UserPlus, Share2, Check, Download, MessageCircle, Twitter, Image } from 'lucide-react';

const TYPE_CONFIG = {
  percentage: { label: 'Percentage Off', icon: Percent, unit: '%', placeholder: 'e.g. 15' },
  fixed: { label: 'Fixed Amount Off', icon: PoundSterling, unit: '£', placeholder: 'e.g. 10' },
  new_client: { label: 'New Client Discount', icon: UserPlus, unit: '%', placeholder: 'e.g. 20' },
};

function generatePromoCard(canvas, { code, discountText, brandName, username }) {
  const ctx = canvas.getContext('2d');
  const w = 1080;
  const h = 1080;
  canvas.width = w;
  canvas.height = h;

  // Background, rich dark
  ctx.fillStyle = '#1A1A1A';
  ctx.fillRect(0, 0, w, h);

  // Subtle pattern, diagonal lines
  ctx.strokeStyle = 'rgba(212,175,55,0.06)';
  ctx.lineWidth = 1;
  for (let i = -h; i < w + h; i += 40) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i + h, h);
    ctx.stroke();
  }

  // Gold accent bar at top
  const grad = ctx.createLinearGradient(0, 0, w, 0);
  grad.addColorStop(0, '#D4AF37');
  grad.addColorStop(1, '#B8962E');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, 6);

  // Zuriya branding
  ctx.fillStyle = '#D4AF37';
  ctx.font = '700 36px Georgia, serif';
  ctx.textAlign = 'center';
  ctx.fillText('ZURIYA', w / 2, 80);

  // Thin separator
  ctx.strokeStyle = 'rgba(212,175,55,0.3)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(w / 2 - 60, 100);
  ctx.lineTo(w / 2 + 60, 100);
  ctx.stroke();

  // Provider name
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '400 28px Georgia, serif';
  ctx.fillText(brandName, w / 2, 160);

  // "EXCLUSIVE OFFER" label
  ctx.fillStyle = 'rgba(212,175,55,0.15)';
  const labelW = 280;
  const labelH = 40;
  const labelX = (w - labelW) / 2;
  const labelY = 210;
  ctx.beginPath();
  ctx.roundRect(labelX, labelY, labelW, labelH, 20);
  ctx.fill();
  ctx.fillStyle = '#D4AF37';
  ctx.font = '600 16px system-ui, sans-serif';
  ctx.fillText('EXCLUSIVE OFFER', w / 2, labelY + 27);

  // Big discount text
  ctx.fillStyle = '#FFFFFF';
  ctx.font = '700 120px Georgia, serif';
  ctx.fillText(discountText, w / 2, 420);

  // "YOUR NEXT BOOKING"
  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  ctx.font = '400 26px system-ui, sans-serif';
  ctx.fillText('YOUR NEXT BOOKING', w / 2, 480);

  // Promo code box
  const boxW = 520;
  const boxH = 100;
  const boxX = (w - boxW) / 2;
  const boxY = 540;

  // Dashed border
  ctx.strokeStyle = '#D4AF37';
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 6]);
  ctx.beginPath();
  ctx.roundRect(boxX, boxY, boxW, boxH, 12);
  ctx.stroke();
  ctx.setLineDash([]);

  // Box fill
  ctx.fillStyle = 'rgba(212,175,55,0.08)';
  ctx.beginPath();
  ctx.roundRect(boxX, boxY, boxW, boxH, 12);
  ctx.fill();

  // "USE CODE" label
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.font = '500 14px system-ui, sans-serif';
  ctx.letterSpacing = '3px';
  ctx.fillText('USE CODE', w / 2, boxY + 32);

  // Code
  ctx.fillStyle = '#D4AF37';
  ctx.font = '700 44px monospace';
  ctx.fillText(code, w / 2, boxY + 78);

  // Booking link
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.font = '400 22px system-ui, sans-serif';
  ctx.fillText(`Book now at zuriya.co.uk/@${username}`, w / 2, 720);

  // Decorative corner accents
  const cornerLen = 30;
  ctx.strokeStyle = '#D4AF37';
  ctx.lineWidth = 2;
  ctx.setLineDash([]);

  // Top-left
  ctx.beginPath();
  ctx.moveTo(30, 30 + cornerLen); ctx.lineTo(30, 30); ctx.lineTo(30 + cornerLen, 30);
  ctx.stroke();
  // Top-right
  ctx.beginPath();
  ctx.moveTo(w - 30 - cornerLen, 30); ctx.lineTo(w - 30, 30); ctx.lineTo(w - 30, 30 + cornerLen);
  ctx.stroke();
  // Bottom-left
  ctx.beginPath();
  ctx.moveTo(30, h - 30 - cornerLen); ctx.lineTo(30, h - 30); ctx.lineTo(30 + cornerLen, h - 30);
  ctx.stroke();
  // Bottom-right
  ctx.beginPath();
  ctx.moveTo(w - 30 - cornerLen, h - 30); ctx.lineTo(w - 30, h - 30); ctx.lineTo(w - 30, h - 30 - cornerLen);
  ctx.stroke();

  // Footer tagline
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.font = '400 18px Georgia, serif';
  ctx.fillText('Multi-Cultural Beauty, Across the UK', w / 2, h - 50);
}

function SharePromoModal({ discount, username, brandName, open, onClose }) {
  const [copied, setCopied] = useState(false);
  const [cardGenerated, setCardGenerated] = useState(false);
  const canvasRef = useRef(null);
  const bookingLink = `https://zuriya.co.uk/@${username}`;
  const discountText = discount.discount_type === 'fixed'
    ? `£${discount.value} off`
    : `${discount.value}% off`;

  const caption = `Hey! Use my code ${discount.code} for ${discountText} your next booking with me on Zuriya.\n\nBook now: ${bookingLink}\n\nJust enter the code at checkout, it's that easy!`;

  // WhatsApp share
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(caption)}`;
  // X/Twitter share
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Use code ${discount.code} for ${discountText} your next booking with me on Zuriya! Book now: ${bookingLink}`)}`;

  const generateCard = useCallback(() => {
    if (!canvasRef.current) return;
    generatePromoCard(canvasRef.current, {
      code: discount.code,
      discountText,
      brandName: brandName || username,
      username,
    });
    setCardGenerated(true);
  }, [discount.code, discountText, brandName, username]);

  useEffect(() => {
    if (open) {
      setCardGenerated(false);
      setCopied(false);
      // Small delay to ensure canvas is in DOM
      setTimeout(generateCard, 100);
    }
  }, [open, generateCard]);

  const downloadCard = () => {
    if (!canvasRef.current) return;
    const link = document.createElement('a');
    link.download = `zuriya-promo-${discount.code}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
    toast.success('Image saved, share it on Instagram or TikTok!');
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
    } catch (err) {
      console.error('Clipboard API failed, using fallback:', err);
      const ta = document.createElement('textarea');
      ta.value = caption;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2500);
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(discount.code);
    } catch (err) {
      console.error('Clipboard API failed, using fallback:', err);
      const ta = document.createElement('textarea');
      ta.value = discount.code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    toast.success(`Code copied: ${discount.code}`);
  };

  const nativeShare = async () => {
    if (!navigator.share) { copyCaption(); return; }
    try {
      const shareData = { title: `${discountText} off on Zuriya`, text: caption };
      // Try sharing with image if possible
      if (canvasRef.current) {
        try {
          const blob = await new Promise(resolve => canvasRef.current.toBlob(resolve, 'image/png'));
          if (blob) {
            const file = new File([blob], `zuriya-promo-${discount.code}.png`, { type: 'image/png' });
            shareData.files = [file];
          }
        } catch (err) { console.error('Image share fallback:', err); }
      }
      await navigator.share(shareData);
    } catch (err) {
      if (err.name !== 'AbortError') copyCaption();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Share2 className="h-5 w-5 text-[#D4AF37]" />
            Share Your Promo Code
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 pt-1" data-testid="share-promo-modal">
          <Tabs defaultValue="card" className="w-full">
            <TabsList className="w-full bg-[#F9F6F0] border border-[#E6E4DD]">
              <TabsTrigger value="card" className="flex-1 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white gap-1.5">
                <Image className="h-3.5 w-3.5" />
                Visual Card
              </TabsTrigger>
              <TabsTrigger value="caption" className="flex-1 data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white gap-1.5">
                <Copy className="h-3.5 w-3.5" />
                Caption
              </TabsTrigger>
            </TabsList>

            {/* ===== VISUAL CARD TAB ===== */}
            <TabsContent value="card" className="space-y-4 mt-4">
              <div className="rounded-lg overflow-hidden border border-[#E6E4DD] bg-[#1A1A1A]" data-testid="promo-card-preview">
                <canvas
                  ref={canvasRef}
                  className="w-full h-auto"
                  style={{ display: cardGenerated ? 'block' : 'none' }}
                />
                {!cardGenerated && (
                  <div className="aspect-square flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]" />
                  </div>
                )}
              </div>
              <Button onClick={downloadCard} className="btn-primary w-full gap-2" data-testid="download-card-btn">
                <Download className="h-4 w-4" />
                Save Image
              </Button>
              <p className="text-xs text-stone-400 text-center">Save and post as an Instagram story, TikTok slide, or WhatsApp status</p>
            </TabsContent>

            {/* ===== CAPTION TAB ===== */}
            <TabsContent value="caption" className="space-y-4 mt-4">
              {/* Promo code highlight */}
              <div className="flex items-center justify-between bg-[#F9F6F0] border border-[#E6E4DD] rounded-lg p-3">
                <div>
                  <p className="text-xs text-stone-400">Promo code</p>
                  <p className="font-mono text-lg font-bold text-[#D4AF37] tracking-wider">{discount.code}</p>
                </div>
                <Button variant="outline" size="sm" onClick={copyCode} className="gap-1" data-testid="share-copy-code-btn">
                  <Copy className="h-3.5 w-3.5" />
                  Copy code
                </Button>
              </div>

              {/* Pre-written caption */}
              <div>
                <Label className="text-xs text-stone-500 mb-1 block">Caption</Label>
                <div className="bg-white border border-[#E6E4DD] rounded-lg p-3 text-sm text-[#1A1A1A] whitespace-pre-wrap leading-relaxed" data-testid="share-caption-text">
                  {caption}
                </div>
              </div>

              <Button onClick={copyCaption} className="btn-primary w-full gap-2" data-testid="share-copy-caption-btn">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied!' : 'Copy Caption'}
              </Button>
            </TabsContent>
          </Tabs>

          {/* ===== SHARE BUTTONS ===== */}
          <div className="border-t border-[#E6E4DD] pt-4">
            <p className="text-xs text-stone-400 mb-3 text-center">Share directly</p>
            <div className="flex items-center justify-center gap-3">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 transition-colors text-sm font-medium"
                data-testid="share-whatsapp-btn"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </a>
              <a
                href={twitterUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1DA1F2]/10 text-[#1DA1F2] hover:bg-[#1DA1F2]/20 transition-colors text-sm font-medium"
                data-testid="share-twitter-btn"
              >
                <Twitter className="h-4 w-4" />
                X / Twitter
              </a>
              <button
                onClick={nativeShare}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors text-sm font-medium"
                data-testid="share-native-btn"
              >
                <Share2 className="h-4 w-4" />
                More
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DiscountsTab({ username, brandName }) {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', discount_type: 'percentage', value: '', max_uses: '', expires_at: '' });
  const [shareDiscount, setShareDiscount] = useState(null);

  useEffect(() => { fetchDiscounts(); }, []);

  const fetchDiscounts = async () => {
    try {
      const res = await api.get('/providers/me/discounts');
      setDiscounts(res.data);
    } catch { toast.error('Failed to load discounts'); }
    finally { setLoading(false); }
  };

  const createDiscount = async () => {
    if (!form.code || !form.value) { toast.error('Code and value are required'); return; }
    try {
      const payload = {
        code: form.code,
        discount_type: form.discount_type,
        value: parseFloat(form.value),
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null,
      };
      const res = await api.post('/providers/me/discounts', payload);
      setDiscounts([res.data, ...discounts]);
      setForm({ code: '', discount_type: 'percentage', value: '', max_uses: '', expires_at: '' });
      setShowForm(false);
      toast.success('Discount code created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create discount');
    }
  };

  const toggleDiscount = async (discountId, isActive) => {
    try {
      await api.put(`/providers/me/discounts/${discountId}`, { is_active: !isActive });
      setDiscounts(discounts.map(d => d.discount_id === discountId ? { ...d, is_active: !isActive } : d));
    } catch { toast.error('Failed to update'); }
  };

  const deleteDiscount = async (discountId) => {
    try {
      await api.delete(`/providers/me/discounts/${discountId}`);
      setDiscounts(discounts.filter(d => d.discount_id !== discountId));
      toast.success('Discount deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Copied: ${code}`);
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
    setForm({ ...form, code });
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37]" /></div>;

  return (
    <div className="space-y-6" data-testid="discounts-tab">
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
            <Tag className="h-5 w-5 text-[#D4AF37]" />
            Discount Codes
          </h3>
          <Button variant="outline" onClick={() => setShowForm(!showForm)} className="text-sm" data-testid="create-discount-btn">
            <Plus className="h-4 w-4 mr-1" />
            {showForm ? 'Cancel' : 'Create Code'}
          </Button>
        </div>

        {showForm && (
          <div className="mb-6 p-4 bg-[#F9F6F0] rounded-lg space-y-3" data-testid="discount-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Discount Type</Label>
                <Select value={form.discount_type} onValueChange={v => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger className="input-zuriya" data-testid="discount-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage Off (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount Off (£)</SelectItem>
                    <SelectItem value="new_client">New Client Discount (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Value *</Label>
                <div className="flex items-center gap-0">
                  <span className="px-3 py-2 bg-white border border-r-0 border-[#E6E4DD] rounded-l-lg text-sm text-stone-500">{TYPE_CONFIG[form.discount_type]?.unit}</span>
                  <Input type="number" value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder={TYPE_CONFIG[form.discount_type]?.placeholder} className="input-zuriya rounded-l-none" data-testid="discount-value-input" />
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Promo Code *</Label>
                <div className="flex gap-2">
                  <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="e.g. WELCOME20" className="input-zuriya flex-1" data-testid="discount-code-input" />
                  <Button variant="outline" onClick={generateCode} className="text-xs px-3 shrink-0">Generate</Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Usage Limit (leave empty for unlimited)</Label>
                <Input type="number" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })} placeholder="e.g. 50" className="input-zuriya" data-testid="discount-max-uses-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Expiry Date (optional)</Label>
              <Input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} className="input-zuriya w-48" data-testid="discount-expiry-input" />
            </div>
            {form.discount_type === 'new_client' && (
              <p className="text-xs text-stone-500 bg-white p-2 rounded">This code will only work for clients who haven't booked with you before.</p>
            )}
            <Button onClick={createDiscount} className="btn-primary" data-testid="save-discount-btn">Create Discount</Button>
          </div>
        )}

        {discounts.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-stone-500 mb-1">No discount codes yet</p>
            <p className="text-xs text-stone-400">Create promo codes to attract new clients or reward regulars</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="discounts-list">
            {discounts.map(d => {
              const cfg = TYPE_CONFIG[d.discount_type] || TYPE_CONFIG.percentage;
              const Icon = cfg.icon;
              const expired = d.expires_at && new Date(d.expires_at) < new Date();
              const maxedOut = d.max_uses && d.times_used >= d.max_uses;
              const isShareable = d.is_active && !expired && !maxedOut;
              return (
                <div key={d.discount_id} className={`flex items-center gap-4 p-4 rounded-lg border ${isShareable ? 'bg-white border-[#E6E4DD]' : 'bg-stone-50 border-stone-200 opacity-70'}`} data-testid={`discount-${d.discount_id}`}>
                  <div className="w-10 h-10 rounded-full bg-[#F9F6F0] flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-[#1A1A1A]">{d.code}</span>
                      <button onClick={() => copyCode(d.code)} className="p-1 hover:bg-stone-100 rounded">
                        <Copy className="h-3 w-3 text-stone-400" />
                      </button>
                      {expired && <span className="text-xs text-red-500 font-medium">Expired</span>}
                      {maxedOut && <span className="text-xs text-amber-600 font-medium">Limit reached</span>}
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5">
                      {d.discount_type === 'fixed' ? `£${d.value} off` : `${d.value}% off`}
                      {d.discount_type === 'new_client' && ' (new clients only)'}
                      {d.max_uses ? ` · ${d.times_used}/${d.max_uses} used` : ` · ${d.times_used} used`}
                      {d.expires_at && ` · Expires ${new Date(d.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                    </p>
                  </div>
                  {isShareable && username && (
                    <button
                      onClick={() => setShareDiscount(d)}
                      className="p-2 hover:bg-amber-50 rounded-lg border border-[#E6E4DD] transition-colors"
                      title="Share this promo code"
                      data-testid={`share-discount-${d.discount_id}`}
                    >
                      <Share2 className="h-4 w-4 text-[#D4AF37]" />
                    </button>
                  )}
                  <Switch checked={d.is_active} onCheckedChange={() => toggleDiscount(d.discount_id, d.is_active)} data-testid={`toggle-discount-${d.discount_id}`} />
                  <button onClick={() => deleteDiscount(d.discount_id)} className="p-2 hover:bg-red-50 rounded" data-testid={`delete-discount-${d.discount_id}`}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Share Promo Modal */}
      {shareDiscount && username && (
        <SharePromoModal
          discount={shareDiscount}
          username={username}
          brandName={brandName}
          open={!!shareDiscount}
          onClose={() => setShareDiscount(null)}
        />
      )}
    </div>
  );
}
